import type { InputHTMLAttributes, Ref } from 'react'
import { DateTimeField } from './DateTimeField'

/**
 * Ein umrandetes Eingabefeld.
 *
 * Zwei Regeln stecken darin: Beschriftung und Feld hängen über die `id`
 * zusammen (nicht über Verschachtelung allein — Sprachausgaben lesen das
 * verlässlicher), und ein Fehler ERSETZT den Hilfetext. Beides gleichzeitig
 * zu zeigen liest sich wie zwei widersprüchliche Ansagen.
 */
export function TextField({
  id,
  label,
  wert,
  onWert,
  hilfe,
  fehler,
  className,
  ref,
  type,
  ...rest
}: {
  id: string
  label: string
  wert: string
  onWert: (wert: string) => void
  hilfe?: string
  fehler?: string
  // React 19 reicht `ref` wie jede andere Angabe durch - es muss nur
  // ausdrücklich angenommen und weitergegeben werden, sonst landet es am
  // Bauteil statt am Feld (die Einkaufsseite setzt darüber den Fokus zurück).
  ref?: Ref<HTMLInputElement>
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'value' | 'onChange' | 'ref'>) {
  if (type === 'date' || type === 'time') {
    return (
      <DateTimeField
        id={id}
        label={label}
        wert={wert}
        onWert={onWert}
        art={type === 'date' ? 'date' : 'time'}
        hilfe={hilfe}
        fehler={fehler}
        className={className}
        ref={ref}
        {...rest}
      />
    )
  }
  const hinweis = fehler ?? hilfe
  const fest =
    type === 'date' ||
    type === 'time' ||
    type === 'month' ||
    type === 'week' ||
    type === 'datetime-local'
  return (
    <div className={`tf${fehler ? ' tf--fehler' : ''}${className ? ` ${className}` : ''}`}>
      <input
        ref={ref}
        id={id}
        type={type}
        className="tf__eingabe"
        value={wert}
        onChange={(e) => onWert(e.target.value)}
        placeholder=" "
        {...(fehler ? { 'aria-invalid': true } : {})}
        {...(hinweis ? { 'aria-describedby': `${id}-hinweis` } : {})}
        {...rest}
      />
      <label className={`tf__label${fest ? ' tf__label--fest' : ''}`} htmlFor={id}>
        {label}
      </label>
      {hinweis ? (
        <p className="tf__hinweis" id={`${id}-hinweis`} {...(fehler ? { role: 'alert' } : {})}>
          {hinweis}
        </p>
      ) : null}
    </div>
  )
}
